import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Upload, Trash2, Download, FolderOpen, Plus,
  ChevronRight, ChevronUp, ChevronDown, Settings, Layers, X, ArrowLeft,
  Loader2, Eye, GripVertical, Edit2, Check, BookOpen
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pres-builder`;

// ==================== THUMBNAIL PREVIEW ====================

const ThumbnailPreview = ({ template, onClose }) => {
  const [thumbMeta, setThumbMeta] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!template) return;
    setLoading(true);
    axios.get(`${API}/templates/${template.id}/thumbnails`)
      .then(res => setThumbMeta(res.data))
      .catch(() => toast.error("Failed to load thumbnails"))
      .finally(() => setLoading(false));
  }, [template]);

  if (!template) return null;

  return (
    <div className="bg-white/[0.03] border border-white/10 p-5 rounded" data-testid="pb-preview-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-mono font-semibold text-gray-200 flex items-center gap-2">
          <Eye className="w-4 h-4 text-teal-400" />
          {template.label}
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="pb-close-preview">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-gray-500 font-mono mb-3">
        {template.slide_count} slide{template.slide_count !== 1 ? "s" : ""} &middot; {template.original_name}
      </p>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
        </div>
      ) : thumbMeta.length === 0 ? (
        <p className="text-xs text-gray-600 font-mono text-center py-8">No thumbnails available</p>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {thumbMeta.map((t, i) => (
            <div key={i} className="border border-white/10 rounded overflow-hidden">
              <img
                src={`${API}/thumbnails/${template.id}/${t.slide_index}`}
                alt={`Slide ${t.slide_index + 1}`}
                className="w-full"
                loading="lazy"
              />
              <div className="px-2 py-1 bg-black/40 text-[10px] text-gray-500 font-mono">
                Slide {t.slide_index + 1}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== BOOKEND CARD ====================

const BookendCard = ({ bookend, type, onUpload, onDelete, uploading }) => {
  const fileRef = useRef(null);
  const label = type === "front" ? "Front Page" : "End Page";
  const color = type === "front" ? "text-emerald-400" : "text-rose-400";
  const borderColor = type === "front" ? "border-emerald-500/30" : "border-rose-500/30";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.pptx')) { toast.error("Only PPTX files"); return; }
    await onUpload(file, type);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className={`bg-white/[0.03] border ${bookend ? borderColor : 'border-white/10 border-dashed'} p-4 rounded`} data-testid={`pb-bookend-${type}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-mono font-semibold uppercase tracking-wider ${color}`}>
          {label}
        </span>
        {bookend && (
          <button onClick={() => onDelete(type)} className="text-gray-600 hover:text-red-400" data-testid={`pb-delete-bookend-${type}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {bookend ? (
        <div className="text-sm font-mono text-gray-300 truncate">{bookend.original_name}</div>
      ) : (
        <>
          <input ref={fileRef} type="file" accept=".pptx" className="hidden" onChange={handleFile} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full py-3 border border-dashed border-white/10 hover:border-teal-500/30 text-gray-500 hover:text-teal-400 text-xs font-mono transition-colors"
            data-testid={`pb-upload-bookend-${type}`}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `Upload ${label} PPTX`}
          </button>
        </>
      )}
    </div>
  );
};

// ==================== MAIN PAGE ====================

export default function PresentationBuilder({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("build");
  const [sections, setSections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [bookends, setBookends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPresAdmin, setIsPresAdmin] = useState(false);

  // Admin state
  const [newSectionName, setNewSectionName] = useState("");
  const [editingSection, setEditingSection] = useState(null);
  const [editSectionName, setEditSectionName] = useState("");
  const [uploadSection, setUploadSection] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadLabels, setUploadLabels] = useState({});
  const [uploading, setUploading] = useState(false);
  const [bookendUploading, setBookendUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Build state
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [outputName, setOutputName] = useState("presentation");
  const [generating, setGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [secRes, tplRes, beRes] = await Promise.all([
        axios.get(`${API}/sections`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/bookends`)
      ]);
      setSections(secRes.data);
      setTemplates(tplRes.data);
      setBookends(beRes.data);
      // Sections collapsed by default
      if (secRes.data.length > 0 && !uploadSection) {
        setUploadSection(secRes.data[0].id);
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [uploadSection]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check pres admin separately so it reacts to user prop
  useEffect(() => {
    console.log('[PresBuilder] user prop:', JSON.stringify(user));
    const email = user?.email;
    if (!email) {
      console.log('[PresBuilder] No email on user, skipping admin check');
      return;
    }
    axios.get(`${API}/admins`).then(res => {
      const adminEmails = (res.data || []).map(a => a.email.toLowerCase());
      const isAdmin = adminEmails.includes(email.toLowerCase());
      console.log('[PresBuilder] Admin check:', email, 'admins:', adminEmails, 'isAdmin:', isAdmin);
      setIsPresAdmin(isAdmin);
    }).catch((err) => {
      console.error('[PresBuilder] Admin check failed:', err);
    });
  }, [user]);

  // --- Section CRUD ---
  const handleAddSection = async () => {
    if (!newSectionName.trim()) return;
    try {
      await axios.post(`${API}/sections`, { name: newSectionName.trim() });
      setNewSectionName("");
      toast.success("Section added");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleRenameSection = async (sectionId) => {
    if (!editSectionName.trim()) return;
    try {
      await axios.put(`${API}/sections/${sectionId}`, { name: editSectionName.trim() });
      setEditingSection(null);
      toast.success("Section renamed");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm("Delete this section and all its templates?")) return;
    try {
      await axios.delete(`${API}/sections/${sectionId}`);
      toast.success("Section deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleMoveSection = async (sectionId, direction) => {
    const idx = sections.findIndex(s => s.id === sectionId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sections.length) return;
    const newOrder = sections.map(s => s.id);
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    try {
      await axios.put(`${API}/sections/reorder`, newOrder);
      fetchData();
    } catch {
      toast.error("Reorder failed");
    }
  };

  // --- Template CRUD ---
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.name.endsWith('.pptx'));
    const newLabels = { ...uploadLabels };
    files.forEach(f => { if (!newLabels[f.name]) newLabels[f.name] = f.name.replace('.pptx', ''); });
    setUploadFiles(prev => [...prev, ...files]);
    setUploadLabels(newLabels);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0 || !uploadSection) return;
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('label', uploadLabels[file.name] || file.name.replace('.pptx', ''));
        fd.append('section_id', uploadSection);
        await axios.post(`${API}/templates`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`${uploadFiles.length} file(s) uploaded`);
      setUploadFiles([]);
      setUploadLabels({});
      fetchData();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      toast.success("Template deleted");
      setSelectedTemplates(prev => prev.filter(id => id !== templateId));
      if (previewTemplate?.id === templateId) setPreviewTemplate(null);
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  // --- Bookend CRUD ---
  const handleBookendUpload = async (file, type) => {
    setBookendUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('bookend_type', type);
      fd.append('label', type === 'front' ? 'Front Page' : 'End Page');
      await axios.post(`${API}/bookends`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${type === 'front' ? 'Front' : 'End'} page uploaded`);
      fetchData();
    } catch {
      toast.error("Upload failed");
    } finally {
      setBookendUploading(false);
    }
  };

  const handleBookendDelete = async (type) => {
    try {
      await axios.delete(`${API}/bookends/${type}`);
      toast.success("Removed");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  // --- Build / Selection ---
  const toggleTemplate = (template) => {
    setSelectedTemplates(prev =>
      prev.includes(template.id) ? prev.filter(id => id !== template.id) : [...prev, template.id]
    );
  };

  const moveSelection = (templateId, direction) => {
    setSelectedTemplates(prev => {
      const idx = prev.indexOf(templateId);
      if (idx < 0) return prev;
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    });
  };

  const handleGenerate = async () => {
    if (selectedTemplates.length === 0) { toast.error("Select at least one template"); return; }
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/merge`, {
        template_ids: selectedTemplates,
        output_name: outputName || "presentation"
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${outputName || 'presentation'}.pptx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Presentation generated!");
    } catch (err) {
      toast.error("Merge failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  const getTemplatesBySection = (sectionId) => templates.filter(t => t.section_id === sectionId);
  const getTemplateById = (id) => templates.find(t => t.id === id);
  const frontBookend = bookends.find(b => b.bookend_type === "front");
  const endBookend = bookends.find(b => b.bookend_type === "end");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d1520" }}>
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#0d1520" }} data-testid="presentation-builder-page">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-30" style={{ background: "#0d1520" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-gray-400 hover:text-white transition-colors" data-testid="pb-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-teal-400" />
              <h1 className="text-lg font-mono font-semibold text-white">Presentation Builder</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />}
            <span className="text-xs text-gray-400 hidden sm:inline">{user?.name || user?.email}</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/10 bg-[#0a0a0c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1">
          {[
            { id: "build", label: "Build Presentation", icon: Layers },
            ...(isPresAdmin ? [{ id: "admin", label: "Admin", icon: Settings }] : [])
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`pb-tab-${tab.id}`}
              className={`px-5 py-3 font-mono text-xs uppercase tracking-wider flex items-center gap-2 border-b-2 transition-colors
                ${activeTab === tab.id ? "border-teal-500 text-teal-400" : "border-transparent text-gray-500 hover:text-gray-300"}`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* ==================== BUILD TAB ==================== */}
        {activeTab === "build" && (
          <div data-testid="pb-build-section">
            {templates.length === 0 ? (
              <div className="bg-white/[0.03] border border-white/10 p-12 text-center rounded">
                <BookOpen className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 font-mono text-sm">No templates in library. Go to Admin to upload PPTX files.</p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-12 gap-6">
                {/* Left: Sections */}
                <div className="lg:col-span-4 space-y-3">
                  <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 px-1">Sections</h3>

                  {sections.map(section => {
                    const sTemplates = getTemplatesBySection(section.id);
                    if (sTemplates.length === 0) return null;
                    const expanded = expandedSections[section.id] === true;
                    return (
                      <div key={section.id} className="bg-white/[0.03] border border-white/10 rounded" data-testid={`pb-section-${section.id}`}>
                        <button
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
                          onClick={() => toggleSection(section.id)}
                        >
                          <h3 className="text-sm font-mono text-gray-200 flex items-center gap-2">
                            <FolderOpen className="w-4 h-4 text-teal-400" /> {section.name}
                            <span className="text-xs text-gray-600">({sTemplates.length})</span>
                          </h3>
                          {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                        </button>
                        {expanded && (
                          <div className="px-3 pb-3 space-y-1.5">
                            {sTemplates.map(tpl => {
                              const isSelected = selectedTemplates.includes(tpl.id);
                              const isPreviewing = previewTemplate?.id === tpl.id;
                              return (
                                <div
                                  key={tpl.id}
                                  className={`flex items-center gap-2 px-3 py-2.5 border rounded transition-all cursor-pointer
                                    ${isSelected ? "border-teal-500/50 bg-teal-500/10" : "border-white/5 hover:border-white/15"}
                                    ${isPreviewing ? "ring-1 ring-teal-400/40" : ""}`}
                                  data-testid={`pb-template-${tpl.id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleTemplate(tpl)}
                                    className="accent-teal-500 flex-shrink-0 cursor-pointer"
                                    data-testid={`pb-check-${tpl.id}`}
                                  />
                                  <div
                                    className="flex-1 min-w-0 cursor-pointer"
                                    onClick={() => setPreviewTemplate(isPreviewing ? null : tpl)}
                                  >
                                    <div className="text-sm font-mono text-gray-200 truncate">{tpl.label}</div>
                                    <div className="text-[10px] text-gray-600 truncate">{tpl.slide_count} slide{tpl.slide_count !== 1 ? 's' : ''}</div>
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setPreviewTemplate(isPreviewing ? null : tpl); }}
                                    className={`p-1 rounded transition-colors flex-shrink-0 ${isPreviewing ? 'text-teal-400' : 'text-gray-600 hover:text-teal-400'}`}
                                    data-testid={`pb-preview-${tpl.id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Middle: Preview */}
                <div className="lg:col-span-4">
                  <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 px-1 mb-3">Preview</h3>
                  {previewTemplate ? (
                    <div className="sticky top-16">
                      <ThumbnailPreview template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
                    </div>
                  ) : (
                    <div className="bg-white/[0.02] border border-white/5 rounded p-8 text-center sticky top-16">
                      <Eye className="w-8 h-8 text-gray-700 mx-auto mb-3" />
                      <p className="text-xs text-gray-600 font-mono">Click the eye icon on any template to preview slides</p>
                    </div>
                  )}
                </div>

                {/* Right: Presentation Merge */}
                <div className="lg:col-span-4">
                  <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 px-1 mb-3">Presentation Merge</h3>
                  <div className="sticky top-16 space-y-4">
                    <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
                      <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-teal-400" />
                        Merge Order ({selectedTemplates.length})
                      </h3>

                      {selectedTemplates.length === 0 ? (
                        <p className="text-xs text-gray-600 font-mono py-2">Select templates from the left to build your presentation.</p>
                      ) : (
                        <div className="space-y-1 mb-3" data-testid="pb-selection-list">
                          {selectedTemplates.map((tid, idx) => {
                            const tpl = getTemplateById(tid);
                            if (!tpl) return null;
                            return (
                              <div key={tid} className="flex items-center gap-2 text-xs font-mono group">
                                <span className="w-5 h-5 flex items-center justify-center bg-teal-500/20 text-teal-400 text-[10px] font-bold rounded">
                                  {idx + 1}
                                </span>
                                <span className="text-gray-300 truncate flex-1">{tpl.label}</span>
                                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => moveSelection(tid, "up")} className="text-gray-600 hover:text-white p-0.5" data-testid={`pb-move-up-${tid}`}>
                                    <ChevronUp className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => moveSelection(tid, "down")} className="text-gray-600 hover:text-white p-0.5" data-testid={`pb-move-down-${tid}`}>
                                    <ChevronDown className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => setSelectedTemplates(prev => prev.filter(id => id !== tid))} className="text-gray-600 hover:text-red-400 p-0.5">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="mt-4 mb-3">
                        <label className="text-xs text-gray-500 font-mono block mb-1">Output filename</label>
                        <input
                          type="text"
                          value={outputName}
                          onChange={(e) => setOutputName(e.target.value)}
                          placeholder="presentation"
                          className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono text-gray-200 rounded
                            focus:outline-none focus:border-teal-500/50"
                          data-testid="pb-output-name"
                        />
                      </div>
                      <button
                        onClick={handleGenerate}
                        disabled={generating || selectedTemplates.length === 0}
                        className="w-full py-3 bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white font-mono text-sm
                          uppercase tracking-wider transition-colors flex items-center justify-center gap-2 rounded"
                        data-testid="pb-generate-btn"
                      >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {generating ? "Merging..." : "Generate & Download"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== ADMIN TAB ==================== */}
        {activeTab === "admin" && isPresAdmin && (
          <div className="space-y-5" data-testid="pb-admin-section">
            {/* Bookends */}
            <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-4 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-amber-400" /> Mandatory Pages
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <BookendCard bookend={frontBookend} type="front" onUpload={handleBookendUpload} onDelete={handleBookendDelete} uploading={bookendUploading} />
                <BookendCard bookend={endBookend} type="end" onUpload={handleBookendUpload} onDelete={handleBookendDelete} uploading={bookendUploading} />
              </div>
            </div>

            {/* Add Section */}
            <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4 text-emerald-400" /> Add Section
              </h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSection()}
                  placeholder="Section name"
                  className="flex-1 bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono text-gray-200 rounded
                    focus:outline-none focus:border-teal-500/50 placeholder-gray-600"
                  data-testid="pb-new-section-input"
                />
                <button
                  onClick={handleAddSection}
                  className="bg-teal-600 hover:bg-teal-500 text-white px-5 py-2 font-mono text-sm rounded"
                  data-testid="pb-add-section-btn"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Sections List */}
            <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-blue-400" /> Sections
              </h3>
              {sections.length === 0 ? (
                <p className="text-xs text-gray-600 font-mono">No sections yet. Add one above.</p>
              ) : (
                <div className="space-y-1.5">
                  {sections.map((s, idx) => (
                    <div key={s.id} className="flex items-center justify-between px-3 py-2.5 border border-white/5 rounded" data-testid={`pb-admin-section-${s.id}`}>
                      {editingSection === s.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="text"
                            value={editSectionName}
                            onChange={(e) => setEditSectionName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleRenameSection(s.id)}
                            className="flex-1 bg-white/5 border border-white/10 px-2 py-1 text-sm font-mono text-gray-200 rounded focus:outline-none focus:border-teal-500/50"
                            autoFocus
                          />
                          <button onClick={() => handleRenameSection(s.id)} className="text-emerald-400 hover:text-emerald-300">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingSection(null)} className="text-gray-500 hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <GripVertical className="w-4 h-4 text-gray-700" />
                            <span className="text-sm font-mono text-gray-200">{s.name}</span>
                            <span className="text-xs text-gray-600 font-mono">({getTemplatesBySection(s.id).length})</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleMoveSection(s.id, "up")} disabled={idx === 0} className="text-gray-600 hover:text-white disabled:opacity-20 p-1">
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleMoveSection(s.id, "down")} disabled={idx === sections.length - 1} className="text-gray-600 hover:text-white disabled:opacity-20 p-1">
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => { setEditingSection(s.id); setEditSectionName(s.name); }} className="text-gray-600 hover:text-teal-400 p-1">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDeleteSection(s.id)} className="text-gray-600 hover:text-red-400 p-1" data-testid={`pb-delete-section-${s.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Templates */}
            <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <Upload className="w-4 h-4 text-amber-400" /> Upload PPTX Templates
              </h3>
              {sections.length === 0 ? (
                <p className="text-xs text-gray-500 font-mono">Create a section first before uploading.</p>
              ) : (
                <>
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 font-mono block mb-1">Target Section</label>
                    <select
                      value={uploadSection}
                      onChange={(e) => setUploadSection(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono text-gray-200 rounded
                        focus:outline-none focus:border-teal-500/50"
                      data-testid="pb-section-select"
                    >
                      {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <input ref={fileInputRef} type="file" accept=".pptx" multiple className="hidden" onChange={handleFileSelect} data-testid="pb-file-input" />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-white/10 hover:border-teal-500/30 p-8 text-center cursor-pointer transition-colors rounded"
                    data-testid="pb-upload-zone"
                  >
                    <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-400 font-mono"><span className="text-teal-400">Click to upload</span> or drag and drop</p>
                    <p className="text-xs text-gray-600 font-mono mt-1">PPTX files only</p>
                  </div>
                  {uploadFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {uploadFiles.map(file => (
                        <div key={file.name} className="flex items-center gap-3 border border-white/5 px-3 py-2 rounded">
                          <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          <span className="text-xs text-gray-400 font-mono flex-shrink-0 truncate max-w-[120px]">{file.name}</span>
                          <input
                            type="text"
                            placeholder="Label"
                            value={uploadLabels[file.name] || ''}
                            onChange={(e) => setUploadLabels({ ...uploadLabels, [file.name]: e.target.value })}
                            className="flex-1 bg-white/5 border border-white/10 px-2 py-1 text-xs font-mono text-gray-200 rounded
                              focus:outline-none focus:border-teal-500/50"
                          />
                          <button onClick={() => { setUploadFiles(uploadFiles.filter(f => f.name !== file.name)); }} className="text-gray-600 hover:text-red-400">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="w-full py-2.5 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white font-mono text-sm
                          flex items-center justify-center gap-2 mt-3 rounded"
                        data-testid="pb-upload-btn"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {uploading ? "Uploading..." : `Upload ${uploadFiles.length} File(s)`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Template Library */}
            {templates.length > 0 && (
              <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
                <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-400" /> Template Library
                </h3>
                {sections.map(section => {
                  const sTemplates = getTemplatesBySection(section.id);
                  if (sTemplates.length === 0) return null;
                  return (
                    <div key={section.id} className="mb-4">
                      <div className="text-xs text-gray-500 font-mono mb-2">{section.name} — {sTemplates.length} file(s)</div>
                      <div className="space-y-1">
                        {sTemplates.map(tpl => (
                          <div key={tpl.id} className="flex items-center gap-3 px-3 py-2 border border-white/5 rounded hover:border-white/10">
                            <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-mono text-gray-200 truncate">{tpl.label}</div>
                              <div className="text-[10px] text-gray-600 truncate">{tpl.original_name} &middot; {tpl.slide_count} slides</div>
                            </div>
                            <button
                              onClick={() => handleDeleteTemplate(tpl.id)}
                              className="text-gray-600 hover:text-red-400 transition-colors p-1"
                              data-testid={`pb-delete-template-${tpl.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
